import AddLinkIcon from "@mui/icons-material/AddLink";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import {
    Alert,
    Autocomplete,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import Property from "crqlar-react-common/dist/components/Property";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {Survey, SurveyStatus, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {
    isJudgingColor,
    nextSurveyColor,
    SURVEY_COLORS,
    surveyColorOf,
    toSurveyColor
} from "crqlar-react-common/dist/utils/surveyColor.util";
import {getNameForLanguage, ISO_LANGS} from "crqlar-react-common/dist/utils/translations.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {FontPickerFields} from "../../common/newFontPicker";
import {surveyFontFamily, surveyFontOf} from "../runner/surveyFont";
import {isRequiredTextMissing, isRequiredValueMissing} from "src/utils/requiredField.util";
import AnonymousSurveyShareDialog from "../share/AnonymousSurveyShareDialog";
import SurveyInviteDialog from "../share/SurveyInviteDialog";
import {useAllSurveys} from "../shared/useAllSurveys";
import SurveyDetailsSection from "./SurveyDetailsSection";
import SurveyLinkField from "./SurveyLinkField";
import SurveyPublishSection from "./SurveyPublishSection";
import SurveyQuestionsPreview from "./SurveyQuestionsPreview";

interface Props {
    surveyData: UseEntityData<Survey>
    updateField: (field: keyof Survey, value: any) => void
}

const clampOptionalCount = (value: number | undefined, min: number, max: number) => {
    if (value === undefined || isNaN(value)) return undefined
    return Math.min(Math.max(value, min), max)
}

const SurveyBasicInfoTab = ({surveyData, updateField}: Props) => {

    const {t} = useTranslation()
    const navigate = useNavigate()
    const sectors = useAllSectors()

    const [createLinkOpen, setCreateLinkOpen] = useState(false)
    const [shareDialogOpen, setShareDialogOpen] = useState(false)
    const readOnly = useReadOnly()

    const survey = surveyData.item
    const surveyId = survey.id
    const nameMissing = isRequiredTextMissing(surveyData, survey.name)
    const typeMissing = isRequiredValueMissing(surveyData, survey.type)
    // Locked once a version exists, not merely while the survey is published: unpublish -> flip -> republish
    // would leave live invitations pointing at a survey whose type no longer accepts them. The server
    // enforces the same rule, so an unpublished-but-already-published survey keeps its type here too.
    const typeLocked = (survey.version ?? 0) > 0
    const published = survey.status === SurveyStatus.PUBLISHED
    const isGuestTypeSurvey = !!survey.type && survey.type !== SurveyType.ANONYMOUS

    // Every other survey's colour, so a new survey shows the one the API will give it.
    const {surveys} = useAllSurveys()
    const otherSurveys = surveys.filter(item => item.id !== surveyId)

    // A survey saved before colours existed shows its stand-in; a new one the colour the API will give it.
    const surveyColor = surveyId
        ? surveyColorOf(survey)
        : toSurveyColor(survey.color) ?? nextSurveyColor(otherSurveys.map(item => item.color))
    const colorHint = isJudgingColor(surveyColor)
        ? t('Charts use green, red, orange and gray to judge figures, so a survey in this color can be misread.')
        : null

    const languageOptions = useMemo(() => ISO_LANGS.map(language => language.code.toLowerCase()), [])

    const linkLanguages = survey.languages

    const fieldGroupGap = {xs: 1.5, sm: 1}

    const sectionGap = {xs: 2.5, sm: 2}

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    /*
        The one page-level alert this tab shows. A survey that is not live is the only state worth
        interrupting the page for - the rest is visible in the fields themselves - so nothing else
        gets a banner here, and a survey that has never been saved is not nagged about it.
    */
    const notPublished = !!survey.id && !published

    const notPublishedAlert = notPublished && (
        <Alert severity={'warning'} sx={{width: '100%'}}>
            {t('Not published yet - guests cannot reach this survey.')}
        </Alert>
    )

    const surveySettingsSection = (
        <SurveyDetailsSection title={t('Settings')}>
            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Name')}
                    tooltip={t('Internal name of this survey, used to find it in lists and reports.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={survey.name || ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Color')}
                    tooltip={t('Tells this survey apart in charts and pickers. Guests never see it.')}
                    labelAlignment={colorHint ? 'top' : undefined}
                >
                    <Stack gap={1} sx={{width: '100%'}}>
                        <NewColorPicker
                            id={'color'}
                            value={surveyColor}
                            presetColors={SURVEY_COLORS}
                            onChange={color => {
                                const next = toSurveyColor(color)
                                if (next) updateField('color', next)
                            }}
                        />
                        {colorHint && <Alert severity={'info'}>{colorHint}</Alert>}
                    </Stack>
                </Property>

                <Property
                    title={t('Type')}
                    tooltip={t('Defines when the survey is collected and what it can be linked to.')}
                    // The lock note sits under the select; the label stays beside the select, not the pair.
                    labelAlignment={typeLocked ? 'top' : undefined}
                >
                    <FormControl fullWidth>
                        <Select
                            id={'type'}
                            value={survey.type ?? ''}
                            onChange={(e) => updateField('type', e.target.value as SurveyType)}
                            size={'small'}
                            error={typeMissing}
                            disabled={typeLocked}
                        >
                            {!survey.type && (
                                <MenuItem value={''} disabled>{t('Not set')}</MenuItem>
                            )}
                            <MenuItem value={SurveyType.PRE_STAY}>{t('Pre stay')}</MenuItem>
                            <MenuItem value={SurveyType.POST_STAY}>{t('Post stay')}</MenuItem>
                            <MenuItem value={SurveyType.PRE_SERVICE}>{t('Pre service')}</MenuItem>
                            <MenuItem value={SurveyType.POST_SERVICE}>{t('Post service')}</MenuItem>
                            <MenuItem value={SurveyType.ANONYMOUS}>{t('Anonymous')}</MenuItem>
                        </Select>
                        {typeLocked && (
                            <Alert severity={'warning'} sx={{mt: 1}}>
                                {t('This survey has been published, so its type can no longer be changed. The links guests have already received and the answers collected so far are tied to this type. To use a different type, create a new survey.')}
                            </Alert>
                        )}
                        {!typeLocked && typeMissing && (
                            <FormHelperText error>{t('Type is required')}</FormHelperText>
                        )}
                    </FormControl>
                </Property>

                <Property
                    title={t('Sectors')}
                    tooltip={t('Limits this survey to the selected sectors. Leave empty to use it everywhere.')}
                >
                    <Autocomplete
                        id={'sectors'}
                        multiple
                        options={sectors}
                        getOptionLabel={(option: Sector) => option.name || ''}
                        value={survey.sectorIds?.map(id => sectors.find(sector => sector.id === id)).filter(Boolean) as Sector[] || []}
                        onChange={(e, value) => updateField('sectorIds', value.map(sector => sector.id))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Languages')}
                    tooltip={t('Languages the survey is offered in. The first one is the fallback language for guests.')}
                >
                    <Autocomplete
                        id={'languages'}
                        multiple
                        options={languageOptions}
                        getOptionLabel={(option: string) => getNameForLanguage(option, true) || option}
                        value={survey.languages ?? []}
                        onChange={(e, value) => updateField('languages', value.map(code => code.toLowerCase()))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Retention days')}
                    tooltip={t('Number of days a response is kept after it was submitted. Leave empty to keep responses forever.')}
                >
                    <CountTextField
                        id={'retention-days'}
                        size={'small'}
                        value={survey.retentionDays ?? ''}
                        onChangeValue={(value) => updateField('retentionDays', clampOptionalCount(value, 0, 365))}
                        InputProps={{inputProps: {min: 0, max: 365}}}
                        fullWidth
                    />
                </Property>
            </Stack>
        </SurveyDetailsSection>
    )

    // Set here rather than in the editor's design panel: the picker's rows need the page's width
    const fontSection = (
        <SurveyDetailsSection title={t('Font')}>
            <Stack gap={fieldGroupGap}>
                <FontPickerFields
                    value={surveyFontOf(survey.theme)}
                    disabled={readOnly}
                    onChange={font => updateField('theme', {...survey.theme, ...font})}
                />

                <Property title={t('Preview')} labelAlignment={'top'}>
                    <Stack gap={1} sx={{border: 1, borderColor: 'divider', borderRadius: 1, p: 2, width: '100%', fontFamily: surveyFontFamily(survey.theme)}}>
                        <Typography component={'div'} sx={{fontFamily: 'inherit', fontSize: 24, fontWeight: 600}}>
                            {t('How was your stay?')}
                        </Typography>
                        <Typography component={'div'} sx={{fontFamily: 'inherit', fontSize: 16}}>
                            {t('The quick brown fox jumps over the lazy dog. 0123456789 !?&%$')}
                        </Typography>
                    </Stack>
                </Property>
            </Stack>
        </SurveyDetailsSection>
    )

    const linksSection = (
        <SurveyDetailsSection title={t('Links')}>
            <Alert severity={'info'} sx={{width: '100%'}}>
                {t('These links appear in the footer of the survey your guests see. Leave one empty and it is simply not shown.')}
            </Alert>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Privacy policy URL')}
                    tooltip={t('Shown to the guest at the bottom of the survey.')}
                    labelAlignment={'top'}
                >
                    <SurveyLinkField
                        id={'privacy-policy-url'}
                        value={survey.privacyPolicyUrl}
                        onChange={(value) => updateField('privacyPolicyUrl', value)}
                        languages={linkLanguages}
                        placeholder={'https://example.com/privacy'}
                    />
                </Property>

                <Property
                    title={t('Terms URL')}
                    tooltip={t('Shown to the guest at the bottom of the survey.')}
                    labelAlignment={'top'}
                >
                    <SurveyLinkField
                        id={'terms-url'}
                        value={survey.termsUrl}
                        onChange={(value) => updateField('termsUrl', value)}
                        languages={linkLanguages}
                        placeholder={'https://example.com/terms'}
                    />
                </Property>

                <Property
                    title={t('Imprint URL')}
                    tooltip={t('Shown to the guest at the bottom of the survey.')}
                    labelAlignment={'top'}
                >
                    <SurveyLinkField
                        id={'imprint-url'}
                        value={survey.imprintUrl}
                        onChange={(value) => updateField('imprintUrl', value)}
                        languages={linkLanguages}
                        placeholder={'https://example.com/imprint'}
                    />
                </Property>

                <Property
                    title={t('Contact URL')}
                    tooltip={t('Shown to the guest at the bottom of the survey.')}
                    labelAlignment={'top'}
                >
                    <SurveyLinkField
                        id={'contact-url'}
                        value={survey.contactUrl}
                        onChange={(value) => updateField('contactUrl', value)}
                        languages={linkLanguages}
                        placeholder={'https://example.com/contact'}
                    />
                </Property>
            </Stack>
        </SurveyDetailsSection>
    )

    const publishSection = (
        <SurveyPublishSection surveyData={surveyData}/>
    )

    // Guest surveys are reached through a personal link, anonymous ones through one shared link.
    const linkAction = isGuestTypeSurvey ? (
        <Button
            variant={'text'}
            startIcon={<AddLinkIcon/>}
            // Open for drafts too: the dialog's Send test is how a draft gets tried; links still need publishing.
            disabled={readOnly}
            onClick={() => setCreateLinkOpen(true)}
        >
            {t('Create link')}
        </Button>
    ) : survey.type === SurveyType.ANONYMOUS ? (
        <Button
            variant={'text'}
            startIcon={<QrCode2Icon/>}
            disabled={!published}
            onClick={() => setShareDialogOpen(true)}
        >
            {t('Share survey')}
        </Button>
    ) : null

    const questionsSection = (
        <SurveyDetailsSection
            title={t('Questions')}
            actions={
                <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'} justifyContent={'flex-end'}>
                    {linkAction && (
                        <Tooltip title={published ? '' : t('Publish the survey first')}>
                            <span>{linkAction}</span>
                        </Tooltip>
                    )}

                    <Button
                        variant={'contained'}
                        endIcon={<ArrowForwardIcon/>}
                        onClick={() => navigate(`/system/surveys/${survey.id}/editor`)}
                        sx={{flexShrink: 0}}
                    >
                        {t('Edit questions')}
                    </Button>
                </Stack>
            }
        >
            <SurveyQuestionsPreview survey={survey}/>
        </SurveyDetailsSection>
    )

    const dangerZoneSection = (
        <Stack gap={sectionGap}>
            <DangerZone
                title={t('Delete survey')}
                description={t('This archives the survey and removes it from the survey list. Already collected submissions stay untouched.')}
                buttonLabel={t('Delete')}
                loading={surveyData.saving}
                onDelete={() => surveyData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {survey.updateDate?.toLocaleString() || '—'}
            </Typography>
        </Stack>
    )

    return (
        <ScreenWrapper>
            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    alignSelf: 'center',
                    gap: {xs: 5, sm: 6},
                }}
            >
                {notPublishedAlert}

                {!!survey.id && (
                    <>
                        {questionsSection}

                        <Divider sx={sectionDividerSx}/>
                        {publishSection}

                        <Divider sx={sectionDividerSx}/>
                    </>
                )}

                {surveySettingsSection}

                <Divider sx={sectionDividerSx}/>
                {fontSection}

                <Divider sx={sectionDividerSx}/>
                {linksSection}

                {!!survey.id && (
                    <Stack
                        sx={{
                            borderTop: 1,
                            borderColor: 'divider',
                            pt: 6,
                        }}
                    >
                        {dangerZoneSection}
                    </Stack>
                )}
            </Stack>
            {createLinkOpen && <SurveyInviteDialog open onClose={() => setCreateLinkOpen(false)} survey={survey}/>}
            {shareDialogOpen && (
                <AnonymousSurveyShareDialog
                    open
                    onClose={() => setShareDialogOpen(false)}
                    survey={survey}
                />
            )}
        </ScreenWrapper>
    )
}

export default SurveyBasicInfoTab
